import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import type {
  GetMediaDowntimeQuery,
  GetMediaUsageQuery,
  MediaDowntimeResponse,
  MediaStatusResponse,
  MediaUsageResponse,
} from '@okvc/schema';

import { ApiClient } from './api.client.js';

export const MediaStatusApiKeys = {
  GET_MEDIA_STATUS: 'GET_MEDIA_STATUS',
  GET_MEDIA_USAGE: 'GET_MEDIA_USAGE',
  GET_MEDIA_DOWNTIME: 'GET_MEDIA_DOWNTIME',
} as const;

export class MediaStatusApi extends ApiClient {
  public static getMediaStatus(): Promise<MediaStatusResponse> {
    return this.http.get('/streams/media-status').then((res) => res.data);
  }

  public static getMediaUsage(params: GetMediaUsageQuery): Promise<MediaUsageResponse> {
    return this.http.get('/streams/media-usage', { params }).then((res) => res.data);
  }

  public static getMediaDowntime(params: GetMediaDowntimeQuery): Promise<MediaDowntimeResponse> {
    return this.http.get('/streams/media-downtime', { params }).then((res) => res.data);
  }
}

export function useMediaStatus(queryOptions?: UseQueryOptions<MediaStatusResponse>) {
  return useQuery<MediaStatusResponse>(
    [MediaStatusApiKeys.GET_MEDIA_STATUS],
    () => MediaStatusApi.getMediaStatus(),
    {
      refetchInterval: 15000,
      keepPreviousData: true,
      ...(queryOptions || {}),
    },
  );
}

export function useMediaUsage(params: GetMediaUsageQuery, queryOptions?: UseQueryOptions<MediaUsageResponse>) {
  return useQuery<MediaUsageResponse>(
    [MediaStatusApiKeys.GET_MEDIA_USAGE, params.granularity, params.month ?? 'all', params.patientId ?? 'all'],
    () => MediaStatusApi.getMediaUsage(params),
    {
      keepPreviousData: true,
      ...(queryOptions || {}),
    },
  );
}

export function useMediaDowntime(params: GetMediaDowntimeQuery, queryOptions?: UseQueryOptions<MediaDowntimeResponse>) {
  return useQuery<MediaDowntimeResponse>(
    [MediaStatusApiKeys.GET_MEDIA_DOWNTIME, params.month ?? 'all', params.patientId ?? 'all'],
    () => MediaStatusApi.getMediaDowntime(params),
    {
      keepPreviousData: true,
      ...(queryOptions || {}),
    },
  );
}
